export type ProductType = 'simple' | 'complex' | 'package'

export const PRODUCT_TYPE_LABELS: Record<ProductType, string> = {
  simple: 'Produs simplu',
  complex: 'Produs complex',
  package: 'Produs pachet',
}

export const PRODUCT_TYPE_DESCRIPTIONS: Record<ProductType, string> = {
  simple: 'Fără câmpuri suplimentare la comandă.',
  complex: 'Poate cere câmpuri suplimentare la adăugarea în coș.',
  package: 'Conține mai multe produse la preț redus de pachet. Clientul alege din fiecare grup configurat câte produse ai setat (minim/maxim). Poți combina mai multe categorii într-un singur grup (ex. Vin + Spritz = o băutură).',
}

export function deriveProductType(isComplex: boolean, isPackage: boolean): ProductType {
  if (isPackage) return 'package'
  if (isComplex) return 'complex'
  return 'simple'
}

export function productIsPackage(product: {
  isPackage?: boolean | null
  productType?: string | null
} | null | undefined): boolean {
  if (!product) return false
  if (typeof product.isPackage === 'boolean') return product.isPackage
  return product.productType === 'package'
}

export function productIsComplex(product: {
  isComplex?: boolean | null
  productType?: string | null
} | null | undefined): boolean {
  if (!product) return false
  if (typeof product.isComplex === 'boolean') return product.isComplex
  return product.productType === 'complex'
}

export function productTypeDescription(
  isComplex: boolean,
  isPackage: boolean,
  isVariable = false,
): string {
  const parts: string[] = []
  if (isVariable) parts.push('Variabil (prețuri pe combinații de atribute)')
  if (isComplex && isPackage) {
    parts.push('Pachet configurabil + câmpuri complexe')
  } else if (isPackage) {
    parts.push(PRODUCT_TYPE_DESCRIPTIONS.package)
  } else if (isComplex) {
    parts.push(PRODUCT_TYPE_DESCRIPTIONS.complex)
  } else if (!isVariable) {
    parts.push(PRODUCT_TYPE_DESCRIPTIONS.simple)
  }
  return parts.join(' · ')
}

export interface ProductPackageItemProduct {
  id: number
  name: string
  sku?: string
  imei?: string
  price: number
  salePrice?: number | null
  stock?: number
  isActive?: boolean
  itemRole?: 'product' | 'equipment' | 'service' | 'extra'
  rolePrices?: {
    equipment?: number | null
    service?: number | null
    extra?: number | null
  } | null
  categoryId?: number | null
  category?: { id: number; name: string; slug?: string } | null
  images?: { id: number; url: string; isPrimary?: boolean }[]
}

export interface ProductPackageItem {
  id?: number
  productId?: number
  itemProductId: number
  quantity: number
  packagePrice: number
  includeInMinPrice?: boolean
  sortOrder?: number
  itemProduct?: ProductPackageItemProduct
}

export interface ProductPackageCategoryRule {
  id?: number
  productId?: number
  /** 0 = fără categorie; altfel categories.id */
  categoryId: number
  minSelect: number
  maxSelect: number
}

export interface ProductPackageChoiceGroup {
  id?: number
  productId?: number
  label: string
  minSelect: number
  maxSelect: number
  categoryIds: number[]
  sortOrder?: number
}

export interface PackageCategoryGroup {
  key: string
  categoryId: number | null
  /** Storage key for rules (0 when no category) */
  ruleCategoryId: number
  categoryIds: number[]
  label: string
  options: ProductPackageItem[]
  minSelect: number
  maxSelect: number
}

export function productEffectivePrice(product: {
  price: number
  salePrice?: number | null
}): number {
  const sale = product.salePrice != null ? Number(product.salePrice) : null
  if (sale != null && !Number.isNaN(sale) && sale > 0) return sale
  return Number(product.price) || 0
}

export function packageItemLinePrice(item: ProductPackageItem): number {
  const qty = Math.max(1, Number(item.quantity) || 1)
  return Math.max(0, Number(item.packagePrice) || 0) * qty
}

export const PACKAGE_MULTI_MAX = 99

function normalizeBounds(
  minSelect: number,
  maxSelect: number,
  optionCount: number,
  allowMultiples = false,
) {
  let min = Math.max(1, Math.floor(minSelect) || 1)
  let max = Math.max(1, Math.floor(maxSelect) || 1)
  if (max < min) max = min
  if (allowMultiples) {
    min = Math.min(min, PACKAGE_MULTI_MAX)
    max = Math.min(max, PACKAGE_MULTI_MAX)
  } else {
    const options = Math.max(1, optionCount)
    min = Math.min(min, options)
    max = Math.min(max, options)
  }
  return { minSelect: min, maxSelect: max }
}

export function countSelectedIds(ids?: number[]): Map<number, number> {
  const counts = new Map<number, number>()
  for (const raw of ids || []) {
    const id = Number(raw)
    if (!Number.isFinite(id) || id <= 0) continue
    counts.set(id, (counts.get(id) || 0) + 1)
  }
  return counts
}

function parseCategoryIds(raw: unknown): number[] {
  let value = raw
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return []
    try {
      value = JSON.parse(trimmed)
    } catch {
      return []
    }
  }
  if (!Array.isArray(value)) return []
  return [...new Set(
    value
      .map((item) => Math.floor(Number(item)))
      .filter((item) => Number.isFinite(item) && item >= 0),
  )]
}

function categoryKeyFromItem(item: ProductPackageItem): number {
  const categoryId = item.itemProduct?.categoryId ?? item.itemProduct?.category?.id ?? null
  return categoryId != null && categoryId > 0 ? categoryId : 0
}

export function groupPackageItemsByCategory(
  items: ProductPackageItem[] | undefined | null,
  rules?: ProductPackageCategoryRule[] | null,
  choiceGroups?: ProductPackageChoiceGroup[] | null,
  allowMultiples = false,
): PackageCategoryGroup[] {
  const storedGroups = (choiceGroups || [])
    .map((group, index) => ({
      key: `group:${index}`,
      label: group.label,
      categoryIds: parseCategoryIds(group.categoryIds),
      minSelect: group.minSelect,
      maxSelect: group.maxSelect,
      sortOrder: group.sortOrder ?? index,
    }))
    .filter((group) => group.categoryIds.length > 0)

  if (storedGroups.length) {
    const categoryToGroup = new Map<number, typeof storedGroups[number]>()
    for (const group of storedGroups) {
      for (const categoryId of group.categoryIds) {
        categoryToGroup.set(categoryId, group)
      }
    }

    const grouped = new Map<string, PackageCategoryGroup>()

    for (const item of items || []) {
      const categoryKey = categoryKeyFromItem(item)
      const stored = categoryToGroup.get(categoryKey)
      const mapKey = stored?.key || (categoryKey > 0 ? `cat:${categoryKey}` : 'none')
      const label = stored?.label
        || item.itemProduct?.category?.name
        || (categoryKey > 0 ? `Categorie #${categoryKey}` : 'Produs')

      const existing = grouped.get(mapKey)
      if (existing) {
        existing.options.push(item)
      } else {
        grouped.set(mapKey, {
          key: mapKey,
          categoryId: categoryKey > 0 ? categoryKey : null,
          ruleCategoryId: categoryKey,
          categoryIds: stored?.categoryIds?.length ? stored.categoryIds : [categoryKey],
          label,
          options: [item],
          minSelect: stored?.minSelect ?? 1,
          maxSelect: stored?.maxSelect ?? 1,
        })
      }
    }

    return [...grouped.values()].map((group) => {
      const bounds = normalizeBounds(
        group.minSelect,
        group.maxSelect,
        group.options.length,
        allowMultiples,
      )
      return { ...group, minSelect: bounds.minSelect, maxSelect: bounds.maxSelect }
    })
  }

  const groups = new Map<string, PackageCategoryGroup>()
  const ruleMap = new Map(
    (rules || []).map((rule) => [Number(rule.categoryId) || 0, rule] as const),
  )

  for (const item of items || []) {
    const categoryKey = categoryKeyFromItem(item)
    const key = categoryKey > 0 ? `cat:${categoryKey}` : 'none'
    const label = item.itemProduct?.category?.name
      || (categoryKey > 0 ? `Categorie #${categoryKey}` : 'Produs')
    const existing = groups.get(key)
    if (existing) {
      existing.options.push(item)
    } else {
      groups.set(key, {
        key,
        categoryId: categoryKey > 0 ? categoryKey : null,
        ruleCategoryId: categoryKey,
        categoryIds: [categoryKey],
        label,
        options: [item],
        minSelect: 1,
        maxSelect: 1,
      })
    }
  }

  for (const group of groups.values()) {
    const rule = ruleMap.get(group.ruleCategoryId)
    const bounds = normalizeBounds(
      rule?.minSelect ?? 1,
      rule?.maxSelect ?? rule?.minSelect ?? 1,
      group.options.length,
      allowMultiples,
    )
    group.minSelect = bounds.minSelect
    group.maxSelect = bounds.maxSelect
  }

  return [...groups.values()]
}

export function buildPackageSelectionsKey(ids?: number[]): string {
  if (!ids?.length) return ''
  return [...ids]
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0)
    .sort((a, b) => a - b)
    .join(',')
}

export function buildPackageCartLineKey(packageItemProductIds?: number[]): string {
  const pk = buildPackageSelectionsKey(packageItemProductIds)
  return pk ? `pkg:${pk}` : ''
}

/** Combined cart line key when a product has package selections and/or complex fields. */
export function buildHybridCartLineKey(
  customFieldsKey?: string,
  packageItemProductIds?: number[],
): string {
  const cf = (customFieldsKey || '').trim()
  const pk = buildPackageSelectionsKey(packageItemProductIds)
  if (cf && pk) return `${cf}|pkg:${pk}`
  if (pk) return `pkg:${pk}`
  return cf
}

export function computePackageUnitPrice(
  items: ProductPackageItem[] | undefined | null,
  selectedItemProductIds: number[],
  rules?: ProductPackageCategoryRule[] | null,
  choiceGroups?: ProductPackageChoiceGroup[] | null,
  allowMultiples = false,
): number {
  const counts = countSelectedIds(selectedItemProductIds)
  let total = 0
  for (const group of groupPackageItemsByCategory(items, rules, choiceGroups, allowMultiples)) {
    for (const row of group.options) {
      const raw = counts.get(row.itemProductId) || 0
      const qty = allowMultiples ? raw : (raw > 0 ? 1 : 0)
      if (qty > 0) total += packageItemLinePrice(row) * qty
    }
  }
  return total
}

/** Lowest package price if customer picks the cheapest options meeting each group min.
 *  When at least one item is flagged `includeInMinPrice`, that explicit set is used instead. */
export function computePackageFromPrice(
  items: ProductPackageItem[] | undefined | null,
  rules?: ProductPackageCategoryRule[] | null,
  choiceGroups?: ProductPackageChoiceGroup[] | null,
  allowMultiples = false,
): number {
  const list = items || []
  const marked = list.filter((item) => item.includeInMinPrice === true)
  if (marked.length) {
    return marked.reduce((sum, item) => sum + packageItemLinePrice(item), 0)
  }

  return groupPackageItemsByCategory(items, rules, choiceGroups, allowMultiples).reduce((sum, group) => {
    const prices = group.options.map(packageItemLinePrice).sort((a, b) => a - b)
    if (!prices.length) return sum
    if (allowMultiples) return sum + prices[0] * group.minSelect
    const take = Math.min(group.minSelect, prices.length)
    return sum + prices.slice(0, take).reduce((s, p) => s + p, 0)
  }, 0)
}

export function buildPackageSnapshots(
  items: ProductPackageItem[] | undefined | null,
  selectedItemProductIds: number[],
  rules?: ProductPackageCategoryRule[] | null,
  choiceGroups?: ProductPackageChoiceGroup[] | null,
  allowMultiples = false,
): Array<{
  fieldId: number
  name: string
  type: 'package_choice'
  value: string
  priceDelta: number
  itemRole?: 'product' | 'equipment' | 'service' | 'extra'
}> {
  const counts = countSelectedIds(selectedItemProductIds)
  const snapshots: Array<{
    fieldId: number
    name: string
    type: 'package_choice'
    value: string
    priceDelta: number
    itemRole?: 'product' | 'equipment' | 'service' | 'extra'
  }> = []

  for (const group of groupPackageItemsByCategory(items, rules, choiceGroups, allowMultiples)) {
    for (const match of group.options) {
      const raw = counts.get(match.itemProductId) || 0
      const pickQty = allowMultiples ? raw : (raw > 0 ? 1 : 0)
      if (pickQty <= 0) continue
      const name = match.itemProduct?.name || `Produs #${match.itemProductId}`
      snapshots.push({
        fieldId: match.itemProductId,
        name: group.label,
        type: 'package_choice',
        value: pickQty > 1 ? `${pickQty} × ${name}` : name,
        priceDelta: packageItemLinePrice(match) * pickQty,
        itemRole: match.itemProduct?.itemRole,
      })
    }
  }

  return snapshots
}

export function packageGroupPickLabel(group: PackageCategoryGroup, allowMultiples = false): string {
  if (allowMultiples) {
    return group.minSelect === 1
      ? `Alege cel puțin 1 din ${group.label} (poți adăuga extra)`
      : `Alege cel puțin ${group.minSelect} din ${group.label} (poți adăuga extra)`
  }
  if (group.minSelect === group.maxSelect) {
    return group.minSelect === 1
      ? `Alege ${group.label}`
      : `Alege ${group.minSelect} din ${group.label}`
  }
  return `Alege ${group.minSelect}–${group.maxSelect} din ${group.label}`
}

export function requiredPackageSelectionCount(
  items: ProductPackageItem[] | undefined | null,
  rules?: ProductPackageCategoryRule[] | null,
  choiceGroups?: ProductPackageChoiceGroup[] | null,
  allowMultiples = false,
): number {
  return groupPackageItemsByCategory(items, rules, choiceGroups, allowMultiples).reduce((sum, group) => sum + group.minSelect, 0)
}

export function packageSelectionsSatisfyRules(
  items: ProductPackageItem[] | undefined | null,
  selectedItemProductIds: number[],
  rules?: ProductPackageCategoryRule[] | null,
  choiceGroups?: ProductPackageChoiceGroup[] | null,
  allowMultiples = false,
): boolean {
  const selected = new Set(selectedItemProductIds.map(Number))
  const counts = countSelectedIds(selectedItemProductIds)
  for (const group of groupPackageItemsByCategory(items, rules, choiceGroups, allowMultiples)) {
    const count = allowMultiples
      ? group.options.reduce((sum, row) => sum + (counts.get(row.itemProductId) || 0), 0)
      : group.options.filter((row) => selected.has(row.itemProductId)).length
    if (allowMultiples) {
      if (count < group.minSelect) return false
      continue
    }
    if (count < group.minSelect || count > group.maxSelect) return false
  }
  return true
}
